import React, { useEffect, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import { components, SingleValueProps, InputActionMeta } from 'react-select';
import Input from '@/elements/Input';
import useLocationAutoComplete from '@/hooks/inputs/useLocationAutoComplete';
import { supportedCountries, Country } from '@/constants/locale/countries';

// Type definitions
interface LocationInputProps {
    value?: string;
    country?: string;
    onChange?: (location: string, country: string) => void;
    onValidationChange?: (params: {
        isValidLocation: boolean;
        isInternational: boolean;
        error: boolean | string;
    }) => void;
}

const SingleValue: React.FC<SingleValueProps<Country>> = ({ children, data, ...props }) => (
    <components.SingleValue {...props} data={data}>
        {data.flag ?? children}
    </components.SingleValue>
);

export default function LocationInput({
    value = '',
    country = '',
    onChange = () => {},
    onValidationChange = () => {},
}: LocationInputProps) {
    const {
        postcode,
        country: currentCountry,
        suggestions,
        isValidLocation,
        handlePostcodeChange,
        handleCountryChange,
        loadMore,
    } = useLocationAutoComplete({
        initialPostcode: value,
        initialCountry: country,
        onValidationChange,
        onLocationChange: onChange,
    });

    const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
    const [recentAction, setRecentAction] = useState<string | null>(null);

    const delayMenu = useCallback(
        debounce((v: boolean) => setMenuIsOpen(v), 500),
        [],
    );

    useEffect(() => {
        delayMenu.cancel();

        // close menu when location is valid and only one suggestion left
        if (isValidLocation && suggestions?.length < 2) {
            delayMenu(false);
            return;
        }

        if (
            recentAction !== 'set-value' &&
            recentAction !== 'menu-close' &&
            recentAction !== 'input-blur'
        ) {
            delayMenu(true);
        }
    }, [suggestions, isValidLocation]);

    const selectedCountry = supportedCountries.find(
        (c) => c.value == currentCountry,
    );

    return (
        <div className="input__location flex">
            <div className="w-14 flex-shrink-0 country-select">
                <Input
                    {...({
                        inputType: "select",
                        options: supportedCountries,
                        value: selectedCountry?.flag || 'GB',
                        inputValue: selectedCountry?.flag || '🇬🇧',
                        handleChange: (v) => handleCountryChange(v.value),
                        filterOption: () => true, // prevent filtering
                        components: { SingleValue },
                        // menuIsOpen: true
                    } as any)}
                />
            </div>
            <Input
                {...({
                    placeholder: `Search ${currentCountry == 'US' ? 'zip code' : 'postcode'} or city...`,
                    value: postcode,
                    handleChange: (v: { label: string; value: string | number }) => handlePostcodeChange(v.label),
                    inputType: "select-creatable",
                    options: suggestions,
                    className: `flex-grow ${suggestions?.length === 0 ? '-empty' : ''} ${
                        menuIsOpen ? '' : '-closed'
                    }`,
                    creatableProps: {
                        value: '',
                        inputValue: postcode,
                        onInputChange: (v: string, actionMeta: InputActionMeta) => {
                            setRecentAction(actionMeta.action);
                            if (
                                actionMeta.action === 'set-value' ||
                                actionMeta.action === 'menu-close' ||
                                actionMeta.action === 'input-blur'
                            ) {
                                setMenuIsOpen(false);
                                return; // Prevent clearing input on blur
                            }
                            handlePostcodeChange(v);
                        },
                        menuIsOpen: true,
                        noOptionsMessage: () => 'Loading...',
                        isValidNewOption: () => false, // disable creating new options
                        blurInputOnSelect: true,
                        captureMenuScroll: true,
                        onMenuScrollToBottom: () => loadMore(),
                    }
                } as any)}
            />
        </div>
    );
}
